import { useState } from 'react'
import { useNavigate } from 'react-router-dom'

import { useErrorHandler } from '../../hooks/useErrorHandler'
import { Booking } from '../../models/Booking'
import { RecordFactory } from '../../models/RecordFactory'
import { Subscription } from '../../models/Subscription'
import { User } from '../../models/User'
import {
    bookingLateInfo,
    cancellationFeeString,
    subscriptionCancellationFeeString,
} from '../../utils/records'
import Button from '../app/design/Button'
import Modal from '../app/design/Modal'
import { Checkbox } from './fields/Checkbox'
import { Chip } from './fields/Chip'
import { TextArea } from './fields/TextArea'
import { cancellationTypeOptions } from '../settings/Options'
import { WorkflowPage } from '../workflows/WorkflowPage'

type Props = {
    isOpen: boolean
    onClose: () => void
    target:
        | { type: 'booking'; booking: Booking; doNotCancelAll: boolean }
        | { type: 'subscription'; subscription: Subscription }
    vendor: User
    me: User
    onCancellationComplete: () => void
}

export const CancellationModal = ({
    isOpen,
    onClose,
    target,
    vendor,
    me,
    onCancellationComplete,
}: Props) => {
    const { logAndDisplayError } = useErrorHandler()
    const navigate = useNavigate()

    const [cancellationType, setCancellationType] = useState<string | null>(null)
    const [cancellationReason, setCancellationReason] = useState('')
    const [shouldEnforceCancellationPolicy, setShouldEnforceCancellationPolicy] = useState(false)

    const [isLoading, setIsLoading] = useState(false)

    const [confirmationModalData, setConfirmationModalData] = useState<{
        title: string
        confirmationButtonText: string
        body: string
        onConfirm: () => void
        secondaryButton?: {
            title: string
            onClick: () => void
        }
    } | null>(null)

    const shouldShowEnforceCheckbox =
        target.type === 'subscription'
            ? target.subscription.shouldShowEnforceCheckboxForCancellation
            : target.booking.shouldShowEnforceCheckboxForCancellation

    const handleSave = () => {
        switch (target.type) {
            case 'booking': {
                if (cancellationType === null) {
                    return
                }

                const booking = target.booking

                const { isCancellableClass, isCancellableSeries, forVendor, forClass, isLate } =
                    bookingLateInfo(booking, booking.item, me, vendor)

                const isRateSet = !!Number(vendor.cancellationRate)
                const offerCancelAll =
                    (isCancellableClass || isCancellableSeries) && !target.doNotCancelAll
                const title =
                    !forVendor && isLate && booking.source && booking.confirmedDate && isRateSet
                        ? `Cancel and pay fee ${cancellationFeeString(vendor)}`
                        : isCancellableSeries || forClass
                          ? 'Cancel this appointment'
                          : 'Cancel appointment'
                let destructiveButtonLabel = offerCancelAll ? 'Cancel this and future dates' : title
                let optionalButtonLabel = offerCancelAll ? title : undefined

                let body = offerCancelAll
                    ? `If you would like to cancel only this appointment choose "${optionalButtonLabel}". To cancel this and future appointments choose "Cancel this and future dates".`
                    : `If you would like to cancel this appointment choose "${destructiveButtonLabel}"`
                if (forVendor && !!booking.channel && !offerCancelAll) {
                    optionalButtonLabel = title
                    destructiveButtonLabel = 'Cancel & notify'
                    body = `If you would like to cancel this appointment and send a notification to the client select "Cancel & notify". To cancel the appointment without notifying the client select "${optionalButtonLabel}".`
                }

                setConfirmationModalData({
                    title,
                    confirmationButtonText: destructiveButtonLabel,
                    body,
                    secondaryButton: optionalButtonLabel
                        ? {
                              title: optionalButtonLabel,
                              onClick: async () => {
                                  setIsLoading(true)
                                  const result = await RecordFactory.getInstance().cancelBooking(
                                      booking,
                                      true,
                                      false,
                                      cancellationType,
                                      cancellationReason,
                                      shouldEnforceCancellationPolicy
                                  )
                                  setIsLoading(false)

                                  if (!result.ok) {
                                      logAndDisplayError(result.error)
                                      return
                                  }

                                  onCancellationComplete()
                              },
                          }
                        : undefined,
                    onConfirm: async () => {
                        setIsLoading(true)
                        const result = await RecordFactory.getInstance().cancelBooking(
                            booking,
                            !booking.channel,
                            offerCancelAll,
                            cancellationType,
                            cancellationReason,
                            shouldEnforceCancellationPolicy
                        )
                        setIsLoading(false)

                        if (!result.ok) {
                            logAndDisplayError(result.error)
                            return
                        }

                        onCancellationComplete()
                    },
                })
                break
            }
            case 'subscription': {
                setConfirmationModalData({
                    title: 'Cancel subscription',
                    confirmationButtonText: 'Cancel subscription',
                    body: shouldEnforceCancellationPolicy
                        ? `By confirming cancellation, you have selected to enforce your policy, charging your client a ${subscriptionCancellationFeeString(vendor)} fee`
                        : 'Cancel this subscription',
                    onConfirm: async () => {
                        setIsLoading(true)
                        const result = await RecordFactory.getInstance().cancelSubscription(
                            target.subscription,
                            shouldEnforceCancellationPolicy
                        )
                        setIsLoading(false)

                        if (!result.ok) {
                            logAndDisplayError(result.error)
                            return
                        }

                        onCancellationComplete()
                    },
                })
                break
            }
        }
    }

    return (
        <Modal
            isOpen={isOpen}
            onClose={onClose}
            title="Cancellation"
            footer={
                <Button
                    disabled={cancellationType === null}
                    onClick={handleSave}
                    isLoading={isLoading}
                >
                    Save
                </Button>
            }
        >
            <div className="flex flex-col items-stretch justify-start gap-y-8">
                <WorkflowPage.IconSection
                    title="Cancellation type (required)"
                    icon="calendarMinus.large"
                >
                    <div className="flex flex-wrap items-center justify-start gap-x-2 gap-y-1">
                        {cancellationTypeOptions.map((option) => (
                            <Chip
                                key={option.value}
                                title={option.label}
                                isSelected={cancellationType === option.value}
                                onClick={() => setCancellationType(option.value)}
                                size="small"
                            />
                        ))}
                    </div>
                </WorkflowPage.IconSection>

                <WorkflowPage.IconSection
                    title="Cancellation reason"
                    icon="note.large"
                    secondaryText={`${cancellationReason.length} / 1200`}
                >
                    <TextArea
                        placeholder="Enter additional reasoning for record"
                        value={cancellationReason}
                        onChange={(event) => setCancellationReason(event.target.value)}
                        onClear={() => setCancellationReason('')}
                        maxLength={1200}
                        className="h-[180px]"
                        size="medium"
                    />
                </WorkflowPage.IconSection>

                {shouldShowEnforceCheckbox && (
                    <WorkflowPage.IconSection
                        title="Enforce cancellation policy"
                        icon="calendarDollar.large"
                        secondaryView={
                            <Checkbox
                                isChecked={shouldEnforceCancellationPolicy}
                                onChange={setShouldEnforceCancellationPolicy}
                            />
                        }
                        description={
                            target.type === 'subscription'
                                ? `${subscriptionCancellationFeeString(vendor)} cancellation fee`
                                : target.booking.package ||
                                    vendor.packageCancellationType === 'charge'
                                  ? `${cancellationFeeString(vendor)} cancellation fee`
                                  : vendor.packageCancellationType === 'session'
                                    ? 'Lose a session'
                                    : `Lose a session & charge a ${cancellationFeeString(vendor)} cancellation fee`
                        }
                        hasHoverEffect
                    />
                )}
            </div>

            {confirmationModalData !== null && (
                <Modal
                    isOpen
                    onClose={() => setConfirmationModalData(null)}
                    size="small"
                    title={confirmationModalData.title}
                    showDividers={false}
                    footer={
                        <div className="flex w-full items-center justify-end gap-x-4">
                            {confirmationModalData.secondaryButton && (
                                <Button
                                    intent="tertiary_deprecated"
                                    onClick={(e: React.MouseEvent) => {
                                        e.stopPropagation()
                                        confirmationModalData.secondaryButton?.onClick()
                                    }}
                                >
                                    {confirmationModalData.secondaryButton.title}
                                </Button>
                            )}
                            <Button
                                intent="primary"
                                onClick={(e: React.MouseEvent) => {
                                    e.stopPropagation()
                                    confirmationModalData.onConfirm()
                                }}
                            >
                                {confirmationModalData.confirmationButtonText}
                            </Button>
                        </div>
                    }
                >
                    {confirmationModalData.body}
                </Modal>
            )}
        </Modal>
    )
}
